import { toRaw, useCookie, watch } from '#imports'
import localforage from 'localforage'
import _ from 'lodash'
import type { OrderType } from '~/store/share.script'
import { COOKIE_OPTIONS, DEFAULT_DISPLAY_MODE } from '~/types/constants'
import { FileOrder, StorageKey, ViewType } from '~/types/enum'
import { getUserIdFromCredentials } from '~/utils/getUserIdFromCredentials'

export interface DisplayModeStruct {
  orderType: OrderType
  pinFoldersTop: boolean
  orderSettingGlobal: boolean
  viewType: ViewType
  viewSettingGlobal: boolean
}

export type SharedDisplayModeStruct = Omit<DisplayModeStruct, 'orderSettingGlobal' | 'viewSettingGlobal'>

export default class DisplayMode {
  private static localforage: LocalForage

  static readonly DEFAULT_DISPLAY_MODE: DisplayModeStruct = {
    orderType: FileOrder.MODIFY_TIME_DESC,
    pinFoldersTop: true,
    orderSettingGlobal: true,
    viewType: ViewType.GRID,
    viewSettingGlobal: true,
  }

  static readonly SHARED_DEFAULT_DISPLAY_MODE: SharedDisplayModeStruct = _.omit(DisplayMode.DEFAULT_DISPLAY_MODE, [
    'orderSettingGlobal',
    'viewSettingGlobal',
  ])

  private static globalKey(): string {
    return `global_${getUserIdFromCredentials() || ''}`
  }

  private static initDB(): LocalForage | undefined {
    try {
      return (DisplayMode.localforage ||= localforage.createInstance({
        name: 'pikpak',
        storeName: 'display_mode',
      }))
    } catch (e) {
      console.error('init display mode db: ', e)
    }
  }

  static async global(): Promise<DisplayModeStruct> {
    try {
      const db = DisplayMode.initDB()
      if (!db) throw Error('db empty')

      return (await db.getItem<DisplayModeStruct>(DisplayMode.globalKey())) || DisplayMode.DEFAULT_DISPLAY_MODE
    } catch (e) {
      console.error('get global display mode: ', e)
      return DisplayMode.DEFAULT_DISPLAY_MODE
    }
  }

  private static async setGlobal(val: DisplayModeStruct): Promise<void> {
    try {
      if (_.isEmpty(val)) return

      const db = DisplayMode.initDB()
      if (!db) throw Error('db empty')

      await db.setItem<DisplayModeStruct>(
        DisplayMode.globalKey(),
        /**
         * 如果view_type 是 list 与 grid 以外的值，修改为 grid，因为云盘只支持这两种类型
         * 云盘的 FileOrder 的声明 key 与 value 是一样的，也需要转换
         */
        toRaw({
          ...val,
          viewType: [ViewType.LIST, ViewType.GRID].includes(val.viewType) ? val.viewType : ViewType.GRID,
          orderType: Object.entries(FileOrder).find(([, v]) => v === val.orderType)?.[0] as OrderType,
        }),
      )
    } catch (e) {
      console.error('set global display mode: ', e)
    }
  }

  private static watchInitialization = false

  static initWatch() {
    if (DisplayMode.watchInitialization) return
    DisplayMode.watchInitialization = true

    const globalDisplayMode = useCookie<DisplayModeStruct>(StorageKey.GLOBAL_DISPLAY_MODE, COOKIE_OPTIONS)

    watch(globalDisplayMode, (val: DisplayModeStruct) => DisplayMode.setGlobal(val))
  }

  static async initDisplayModeInCookie() {
    // 只在 Client 环境执行，Server 环境不执行
    const globalCookieDisplayMode = useCookie<DisplayModeStruct | undefined>(
      StorageKey.GLOBAL_DISPLAY_MODE,
      COOKIE_OPTIONS,
    )
    globalCookieDisplayMode.value ||= await DisplayMode.global()

    // NOTICE 云盘与分享的 orderType 的值不同，需要转换
    if (!Object.values(FileOrder).includes(globalCookieDisplayMode.value.orderType)) {
      /** 云盘的 FileOrder 的声明 key 与 value 是一样的，可以匹配当前项目的 key */
      globalCookieDisplayMode.value.orderType =
        // @ts-expect-error 类型约束变小了
        FileOrder[globalCookieDisplayMode.value.orderType] || DEFAULT_DISPLAY_MODE.orderType
    }

    const sharedCookieDisplayMode = useCookie<SharedDisplayModeStruct | undefined>(
      StorageKey.SHARED_DISPLAY_MODE,
      COOKIE_OPTIONS,
    )
    sharedCookieDisplayMode.value ||= DisplayMode.SHARED_DEFAULT_DISPLAY_MODE
  }
}
